import React, { FC, useCallback, useState } from 'react';
import { T } from 'src/shared/helpers';
import {
    PropertiesPanelAccordion,
    PropertiesPanelRangeProperty,
    PropertiesPanelSection,
    PropertiesPanelSelectProperty,
    PropertiesPanelSwitch,
    PropertiesPanelSwitchProperty
} from '../../../../../widgets/propertiesPanel';
import InspectorShell from './inspectorShell';
import { SHOW_NOT_YET_AVAILABLE } from './notYetAvailable';

// EFFECTS — cosa succede all'immagine DOPO che la scena è stata disegnata.
//
// Di tutto il pannello del prototipo, il backoffice sa fare la prima metà: il tone mapping e
// il contrasto vivono sul config della camera (`SceneCameraConfig`: `toneMappingEnabled`,
// `toneMappingType`, `exposure`, `contrast`) e il motore ha i suoi setter.
//
// DEI SEI FILTRI, UNO C'È DAVVERO ed è il GLOW: `SceneGlowConfig` nel modello, `glow` nel
// salvataggio, e sei metodi sul motore. Era in elenco fra i «non disponibili» sotto il nome
// «Bloom», che è anche il motivo per cui non si vedeva: sono due cose vicine ma diverse — un
// bloom fa sbordare qualunque cosa sia luminosa, il GlowLayer di Babylon fa brillare ciò che è
// EMISSIVO. Chiamarlo col suo nome è la differenza fra una funzione che c'è e una che manca.
//
// Il resto — tinta, saturazione, luminosità e gli altri cinque filtri — **non esiste**: né campo
// nel config, né metodo sul motore. Le righe sono scritte e spente, e si disegnano solo con
// `SHOW_NOT_YET_AVAILABLE` acceso: chi aggiunge il motore trova il posto già pronto invece di
// doverlo inventare.
//
// `toneMappingType` è nel config ma **non ha un setter sul motore**: il modo si sceglie e si
// salva, e si vedrà quando il setter arriva. È scritto nel registro dei punti non supportati.

// LA TESTATA E I CAPITOLI NON SI DISEGNANO PIÙ QUI: erano gli stessi di `worldEditor`, riscritti
// una seconda volta, e a forza di riscriverli le due copie avevano smesso di assomigliarsi.

// Babylon: ImageProcessingConfiguration.TONEMAPPING_STANDARD = 0, TONEMAPPING_ACES = 1.
const TONE_MAPPING_OPTIONS = [
    { value: -1, label: T._('None', 'Admin') },
    { value: 0, label: T._('Standard', 'Admin') },
    { value: 1, label: T._('ACES Filmic', 'Admin') }
];

const FILTERS = ['Ambient occlusion', 'Depth of field', 'Vignette', 'Film grain', 'Chromatic aberration'];

interface EffectsEditorProps {
    sceneEditor?: any;
    zkScene?: any;
}

const EffectsEditor: FC<EffectsEditorProps> = ({ sceneEditor, zkScene }) => {
    const cameraConfig = zkScene?.getOrCreateCameraConfig?.();
    const glowConfig = zkScene?.getOrCreateGlowConfig?.();

    const initialMode = (cameraConfig?.get?.('toneMappingEnabled') ?? false) ? (cameraConfig?.get?.('toneMappingType') ?? 1) : -1;
    const [toneMappingMode, setToneMappingMode] = useState<number>(initialMode);
    const [exposure, setExposure] = useState<number>(cameraConfig?.get?.('exposure') ?? 1);
    const [contrast, setContrast] = useState<number>(cameraConfig?.get?.('contrast') ?? 1);

    // IL CONFIG PRIMA DEL MOTORE: è il config che il salvataggio manda al server, quindi è lui a
    // dire com'è fatta questa scena. Il motore risponde per la scena che sta disegnando ADESSO, e
    // serve come ripiego quando il config non porta ancora niente.
    const [glow, setGlow] = useState<boolean>(glowConfig?.get?.('enabled') ?? sceneEditor?.getGlowEnabled?.() ?? false);
    const [glowIntensity, setGlowIntensity] = useState<number>(glowConfig?.get?.('intensity') ?? sceneEditor?.getGlowIntensity?.() ?? 1);
    const [glowBlur, setGlowBlur] = useState<number>(glowConfig?.get?.('blurSize') ?? sceneEditor?.getGlowBlurSize?.() ?? 16);

    const changeToneMapping = useCallback(
        (mode: number) => {
            setToneMappingMode(mode);
            sceneEditor?.setViewerCameraToneMappingEnabled?.(mode >= 0);
            // Il tipo si salva anche senza setter sul motore: il config è ciò che il
            // salvataggio manda al server, e domani il motore lo rileggerà da lì.
            if (mode >= 0) cameraConfig?.set?.('toneMappingType', mode);
        },
        [cameraConfig, sceneEditor]
    );

    const changeExposure = useCallback(
        (value: number) => {
            setExposure(value);
            sceneEditor?.setViewerCameraExposure?.(value);
        },
        [sceneEditor]
    );

    const changeContrast = useCallback(
        (value: number) => {
            setContrast(value);
            sceneEditor?.setViewerCameraContrast?.(value);
        },
        [sceneEditor]
    );

    // I TRE SETTER DEL GLOW SCRIVONO SOLO IL MOTORE. A differenza dei `setViewer*`, che il config
    // lo scrivono da sé, questi toccano il GlowManager e basta: da soli sarebbero una modifica che
    // si vede e che ricaricando non c'è più. Quindi si scrivono tutti e due — il motore per
    // vederlo subito, il config perché è ciò che si salva.
    const changeGlow = useCallback(
        (value: boolean) => {
            setGlow(value);
            sceneEditor?.setGlowEnabled?.(value);
            glowConfig?.set?.('enabled', value);
        },
        [sceneEditor, glowConfig]
    );

    const changeGlowIntensity = useCallback(
        (value: number) => {
            setGlowIntensity(value);
            sceneEditor?.setGlowIntensity?.(value);
            glowConfig?.set?.('intensity', value);
        },
        [sceneEditor, glowConfig]
    );

    const changeGlowBlur = useCallback(
        (value: number) => {
            setGlowBlur(value);
            sceneEditor?.setGlowBlurSize?.(value);
            glowConfig?.set?.('blurSize', value);
        },
        [sceneEditor, glowConfig]
    );

    return (
        <InspectorShell kind='effects' name={T._('Effects', 'Admin')} specs={T._('Color grading · filters', 'Admin')}>
            <PropertiesPanelSection title={T._('Color', 'Admin')}>
                <PropertiesPanelAccordion header={T._('Tone mapping', 'Admin')}>
                    <PropertiesPanelSelectProperty
                        id='effects-tonemapping-mode'
                        label={T._('Mode', 'Admin')}
                        options={TONE_MAPPING_OPTIONS}
                        value={TONE_MAPPING_OPTIONS.find((option) => option.value === toneMappingMode) ?? TONE_MAPPING_OPTIONS[0]}
                        onChange={(option) => changeToneMapping(Number(option?.value ?? -1))}
                    />
                    <PropertiesPanelRangeProperty
                        id='effects-exposure'
                        label={T._('Exposure', 'Admin')}
                        value={exposure}
                        min={0}
                        max={4}
                        step={0.01}
                        onChange={changeExposure}
                    />
                </PropertiesPanelAccordion>

                <PropertiesPanelAccordion header={T._('Color grading', 'Admin')}>
                    {/* Tinta, saturazione e luminosità non hanno un campo nel config né un metodo
                        sul motore: la riga è scritta e spenta, e si mostra solo con
                        `SHOW_NOT_YET_AVAILABLE` acceso. */}
                    {SHOW_NOT_YET_AVAILABLE && (
                        <>
                            <PropertiesPanelRangeProperty
                                id='effects-hue'
                                label={T._('Hue', 'Admin')}
                                value={0}
                                min={-180}
                                max={180}
                                step={1}
                                unit='°'
                                disabled
                            />
                            <PropertiesPanelRangeProperty
                                id='effects-saturation'
                                label={T._('Saturation', 'Admin')}
                                value={0}
                                min={-1}
                                max={1}
                                step={0.01}
                                disabled
                            />
                            <PropertiesPanelRangeProperty
                                id='effects-brightness'
                                label={T._('Brightness', 'Admin')}
                                value={0}
                                min={-1}
                                max={1}
                                step={0.01}
                                disabled
                            />
                        </>
                    )}
                    <PropertiesPanelRangeProperty
                        id='effects-contrast'
                        label={T._('Contrast', 'Admin')}
                        value={contrast}
                        min={0}
                        max={4}
                        step={0.01}
                        onChange={changeContrast}
                    />
                </PropertiesPanelAccordion>
            </PropertiesPanelSection>

            <PropertiesPanelSection title={T._('Filters', 'Admin')}>
                {/* IL PRIMO FILTRO CHE ESISTE DAVVERO, e ha la forma che il mockup dà a tutti: una card
                    con l'interruttore in testata e dentro i suoi parametri. Si apre da sé quando lo
                    accendi — si accende una funzione per usarla. */}
                <PropertiesPanelAccordion
                    header={T._('Glow', 'Admin')}
                    openWhen={glow}
                    trailing={<PropertiesPanelSwitch id='effects-glow' ariaLabel={T._('Glow', 'Admin')} checked={glow} onChange={changeGlow} />}
                >
                    <PropertiesPanelRangeProperty
                        id='effects-glow-intensity'
                        label={T._('Intensity', 'Admin')}
                        value={glowIntensity}
                        min={0}
                        max={2}
                        step={0.05}
                        onChange={changeGlowIntensity}
                        disabled={!glow}
                    />
                    <PropertiesPanelRangeProperty
                        id='effects-glow-blur'
                        label={T._('Blur size', 'Admin')}
                        value={glowBlur}
                        min={0}
                        max={64}
                        step={1}
                        onChange={changeGlowBlur}
                        disabled={!glow}
                    />
                </PropertiesPanelAccordion>

                {/* NEL MOCKUP OGNI FILTRO È UNA CARD CHE SI APRE, con il suo interruttore in testata e
                    dentro i suoi parametri. Qui i parametri non esistono — nessuno dei sei ha un metodo
                    sul motore — e una card che si apre sul vuoto promette qualcosa che non c'è. Resta il
                    controllo giusto, l'interruttore, spento e non toccabile: dice che il filtro è
                    previsto e che oggi non si accende — quando `SHOW_NOT_YET_AVAILABLE` lo lascia
                    disegnare. Quando il motore arriverà, ognuno di questi diventa una card e i
                    parametri ci entrano dentro. */}
                {SHOW_NOT_YET_AVAILABLE &&
                    FILTERS.map((filter) => (
                        <PropertiesPanelSwitchProperty
                            key={filter}
                            id={`effects-filter-${filter.toLowerCase().replace(/\s+/g, '-')}`}
                            label={T._(filter, 'Admin')}
                            caption={T._('Not available yet', 'Admin')}
                            checked={false}
                            disabled
                        />
                    ))}
            </PropertiesPanelSection>
        </InspectorShell>
    );
};

export default EffectsEditor;
